import { StrictMode, type ReactNode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';

import App from './app/app';
import { appTheme } from './app/theme';
import './styles.scss';

export type MainAppRouterType = 'browser' | 'memory';

export interface MainAppMountOptions {
  basename?: string;
  initialPath?: string;
  router?: MainAppRouterType;
  strictMode?: boolean;
}

export interface MainAppMountHandle {
  unmount: () => void;
}

const mountedRoots = new WeakMap<HTMLElement, ReactDOM.Root>();

const MainAppRoot = () => (
  <ThemeProvider theme={appTheme}>
    <App />
  </ThemeProvider>
);

const withRouter = (options: MainAppMountOptions, children: ReactNode) => {
  const router = options.router ?? 'browser';
  const basename = options.basename;

  if (router === 'memory') {
    return (
      <MemoryRouter basename={basename} initialEntries={[options.initialPath ?? '/']}>
        {children}
      </MemoryRouter>
    );
  }

  return <BrowserRouter basename={basename}>{children}</BrowserRouter>;
};

const resolveContainer = (target: string | HTMLElement) => {
  if (typeof target === 'string') {
    const node = document.querySelector<HTMLElement>(target);
    if (!node) {
      throw new Error(`Could not find main app mount target: ${target}`);
    }

    return node;
  }

  return target;
};

export const mountMainApp = (
  target: string | HTMLElement,
  options: MainAppMountOptions = {}
): MainAppMountHandle => {
  const container = resolveContainer(target);
  const existingRoot = mountedRoots.get(container);

  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = ReactDOM.createRoot(container);
  mountedRoots.set(container, root);

  const app = withRouter(options, <MainAppRoot />);
  const shouldUseStrictMode = options.strictMode ?? false;

  root.render(shouldUseStrictMode ? <StrictMode>{app}</StrictMode> : app);

  return {
    unmount: () => {
      const currentRoot = mountedRoots.get(container);
      if (!currentRoot) {
        return;
      }

      currentRoot.unmount();
      mountedRoots.delete(container);
    },
  };
};

declare global {
  interface Window {
    DotstormingMainApp?: {
      mountMainApp: typeof mountMainApp;
    };
  }
}

window.DotstormingMainApp = {
  mountMainApp,
};
